import type React from "react";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import toast from "react-hot-toast";

import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import { BellIcon, ListIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, type ButtonProps } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

import IdeaProjectEventItem from "./IdeaProjectEventItem";
import { type IProjectIdeaEvent, hasEventType } from "./ideaEventUtils";
import {
  getBoardEventsQueryKey,
  updateBoardEventInCache,
  useBoardEventsInfiniteQuery,
} from "./useBoardEventsInfiniteQuery";

type BoardEventsPage = OpenAPI.GET<"/boards/{for_id}/events">["response"];

const EventContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 6px 0 0;
  max-height: 600px;
  min-height: 100px;
  overflow: auto;
  scrollbar-width: thin;
`;

const BoardEvents = ({
  forId,
  setCurrentIdeaId,
  buttonStyle,
  buttonProps = {},
}: {
  forId: string;
  setCurrentIdeaId: Dispatch<SetStateAction<string>>;
  buttonStyle?: React.CSSProperties;
  buttonProps?: ButtonProps;
}) => {
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const [seenEvents, setSeenEvents] = useState<boolean>(true);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [challenge, setChallenge] = useState<
    OpenAPI.GET<"/challenges/{id}">["response"] | null
  >(null);
  const bottomOfEventsRef = useRef<HTMLDivElement | null>(null);
  const prevPageCountRef = useRef(0);
  const userId = user?._id;
  const theme = useTheme();
  const defaultLaneName =
    challenge?.projectBoard?.defaultLane?.name ?? "Default";

  const {
    events,
    seen,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    data,
  } = useBoardEventsInfiniteQuery({ forId, limit: 25 });

  useEffect(() => {
    setSeenEvents(seen);
  }, [seen]);

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  useEffect(() => {
    const pageCount = data?.pages.length ?? 0;
    if (pageCount > prevPageCountRef.current && pageCount > 1) {
      bottomOfEventsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
    prevPageCountRef.current = pageCount;
  }, [data?.pages.length]);

  useEffect(() => {
    if (!forId) return;
    api.challenges.get(
      forId,
      (challengeData) => {
        if (challengeData) setChallenge(challengeData);
      },
      () => {},
    );
  }, [forId]);

  const onEventClick = useCallback(
    (event: IProjectIdeaEvent) => {
      setPopupOpen(false);
      setCurrentIdeaId(event.idea);
      api.boards.markBoardEventAsInteracted(
        forId,
        event._id,
        () => {
          queryClient.setQueryData<InfiniteData<BoardEventsPage>>(
            getBoardEventsQueryKey(forId, { limit: 25 }),
            (currentData) =>
              updateBoardEventInCache(currentData, event._id, userId ?? ""),
          );
        },
        () => {},
      );
    },
    [forId, queryClient, setCurrentIdeaId, userId],
  );

  const onOpen = useCallback(() => {
    setPopupOpen(true);
    api.boards.markBoardEventsAsSeen(
      forId,
      () => setSeenEvents(true),
      () => {},
    );
  }, [forId]);

  if (!events.length && !isLoading) return null;

  return (
    <Popup
      onClose={() => setPopupOpen(false)}
      open={popupOpen}
      on="click"
      wide="very"
      trigger={
        <Button
          color={!seenEvents ? "red" : undefined}
          style={{ ...(buttonStyle ?? {}) }}
          size="small"
          {...buttonProps}
        >
          <Icon icon={seenEvents ? ListIcon : BellIcon!} />
          {theme.sizes.isLargeComputer ? "Recent activity" : null}
        </Button>
      }
      onOpen={onOpen}
      position="bottom right"
    >
      {
        <EventContainer>
          {isLoading ? (
            <Loader style={{ margin: 10 }} />
          ) : (
            <>
              {events.map((event) => {
                if (!hasEventType(event.action)) return null;
                return (
                  <IdeaProjectEventItem
                    key={event._id}
                    event={event}
                    showIdeaName
                    defaultLaneName={defaultLaneName}
                    onClick={() => onEventClick(event)}
                    interacted={
                      !!userId && !!event.interacted?.includes(userId)
                    }
                  />
                );
              })}
              {isFetchingNextPage ? <Loader style={{ margin: 10 }} /> : null}
              {hasNextPage && !isFetchingNextPage ? (
                <Button
                  size="small"
                  onClick={() => fetchNextPage()}
                  style={{
                    marginTop: 5,
                    width: "99%",
                    minHeight: 30,
                    padding: 0,
                  }}
                >
                  Load more
                </Button>
              ) : null}
              <div ref={bottomOfEventsRef} />
            </>
          )}
        </EventContainer>
      }
    </Popup>
  );
};

export default BoardEvents;
